import React, { useContext, useState, useEffect } from "react";
import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import { useMutation } from "@apollo/client";
import { useMutation as mutationDeleteImage } from "react-query";
//Icons
import { BiMinusCircle } from "react-icons/bi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { CREATE_GROUP, UPDATE_GROUP } from "../../Schema/Group";

export default function GroupForm({
  open,
  handleClose,
  setRefetch,
  editData,
  dialogTitle,
}) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  //======================== Create =========================
  const [createGroup] = useMutation(CREATE_GROUP, {
    onCompleted: ({ createGroup }) => {
      // console.log("createGroup::", createGroup);
      setLoading(false);
      if (createGroup?.is_success === true) {
        setAlert(true, "success", createGroup?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createGroup?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });
  //======================== Update =========================
  const [updateGroup] = useMutation(UPDATE_GROUP, {
    onCompleted: ({ updateGroup }) => {
      // console.log("updateGroup::", updateGroup);
      setLoading(false);
      if (updateGroup?.is_success === true) {
        setAlert(true, "success", updateGroup?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateGroup?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    group_name: Yup.string().required(t("require")),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      group_name: "",
      remark: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (submitValues) => {
      //   console.log("submitValues:::", submitValues);
      setLoading(true);
      if (dialogTitle === "Create") {
        createGroup({
          variables: {
            input: {
              group_name: submitValues?.group_name?.trim(),
              remark: submitValues?.remark,
            },
          },
        });
      } else {
        updateGroup({
          variables: {
            id: editData?._id,
            input: {
              group_name: submitValues?.group_name?.trim(),
              remark: submitValues?.remark,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (editData && dialogTitle === "Update") {
      setFieldValue("group_name", editData?.group_name);
      setFieldValue("remark", editData?.remark);
    }
  }, [editData]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {dialogTitle === "Create"
                ? t("dialog_title_create_group")
                : t("dialog_title_update_group")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("group_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("group_name")}
                  {...getFieldProps("group_name")}
                  error={Boolean(touched.group_name && errors.group_name)}
                  helperText={touched.group_name && errors.group_name}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("remark")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder={t("remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
              {dialogTitle === "Create" ? t("btn-create") : t("btn-update")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
